'use client';

import {
  Box,
  IconButton,
  Link,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material';
import Optional from '../../../Optional';
import RichText, { RichTextContent } from '../../../RichText';
import { trackEvent } from '../../../../utils/trackEvent';

type Props = {
  label?: string;
  value: string;
  info?: RichTextContent;
  icon?: React.ReactNode;
  href?: string;
  labelVariant?: TypographyProps['variant'];
  valueVariant?: TypographyProps['variant'];
  noLink?: boolean;
};

const getContactType = (href: string | undefined) => {
  if (href?.startsWith('tel:')) return 'phone' as const;
  if (href?.startsWith('mailto:')) return 'email' as const;
  return undefined;
};

const InfoElement = ({
  label,
  value,
  icon,
  href,
  info,
  labelVariant,
  valueVariant,
  noLink,
}: Props) => {
  const contactType = getContactType(href);

  const handleClick = () => {
    if (contactType) {
      trackEvent('select_content', { content_type: contactType, item_id: value });
    }
  };

  const handleCopy = () => {
    if (contactType) {
      trackEvent('select_content', { content_type: contactType, item_id: value });
    }
  };

  return (
    <Stack key={value} spacing={1} sx={{ color: 'text.primary' }}>
      <Typography
        variant={labelVariant || 'subtitle2'}
        component={'span'}
        sx={{ fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Stack direction={'row'} spacing={2} sx={{ alignItems: 'center' }}>
        <Optional condition={!!icon}>
          <IconButton
            size={'small'}
            sx={{
              border: '1px solid gray',
              borderColor: 'text.secondary',
            }}
            component={Link}
            href={href || value}
            onClick={handleClick}
            onCopy={handleCopy}
          >
            {icon}
          </IconButton>
        </Optional>
        <Typography
          variant={valueVariant || 'h5'}
          component={!icon && href && !noLink ? Link : 'p'}
          href={!noLink ? href : undefined}
          onClick={handleClick}
          onCopy={handleCopy}
        >
          {value}
        </Typography>
      </Stack>
      <Optional condition={!!info}>
        <Box sx={{ pt: 1 }}>
          <RichText>{info}</RichText>
        </Box>
      </Optional>
    </Stack>
  );
};

export default InfoElement;
